import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import type { EntryViewT } from "../../../types/database/entries";
import {
	convertEntriesToChartConfig,
	convertEntriesToChartData,
} from "./utils";

export function OxfaqChart({
	entries,
	formId,
	disableAnimations = false,
}: {
	entries: EntryViewT[];
	formId: string;
	disableAnimations?: boolean;
}) {
	const chartConfig = convertEntriesToChartConfig(entries, formId);
	const chartData = convertEntriesToChartData(entries, formId);

	console.log(chartData);

	const formatPercentage = (value: number, max: number) => {
		return `${((value / max) * 100).toFixed(0)}%`;
	};

	return (
		<ChartContainer config={chartConfig}>
			<AreaChart accessibilityLayer data={chartData}>
				<CartesianGrid vertical={false} />
				<XAxis
					dataKey="date"
					tickLine={false}
					tickMargin={10}
					axisLine={false}
					tickFormatter={(value) => {
						return dayjs(value).format("DD MMM");
					}}
				/>
				<YAxis
					domain={[0, 64]}
					tickLine={false}
					axisLine={false}
					tickMargin={10}
					label={{
						value: "MOxFQ Score",
						angle: -90,
						position: "insideLeft",
					}}
				/>
				<ChartTooltip
					content={
						<ChartTooltipContent
							hideLabel
							className="p-3 w-[200px] md:w-[300px]"
							formatter={(value, name, item, index) => (
								<div className="flex flex-col gap-2">
									{index === 0 && (
										<>
											<div className="flex flex-col">
												<span className="mb-1 text-[0.65rem] text-slate-400 uppercase tracking-wider">
													Timing
												</span>
												<span className="font-medium text-slate-700 text-sm">
													{item.payload.timing}
												</span>
												<div className="mt-1 text-slate-600 text-xs">
													{dayjs(item.payload.date).format("DD MMM YYYY")}
												</div>
											</div>

											<div className="flex flex-col bg-slate-50 shadow-sm p-2 border border-slate-200 rounded-md">
												<span className="mb-1 font-bold text-[0.65rem] text-slate-500 uppercase tracking-wider">
													MOxFQ Total Score
												</span>
												<span className="flex items-center font-semibold text-slate-900 text-lg">
													<span className="text-indigo-600">
														{item.payload["MOxFQ Total Score"]}/64
													</span>
													<span className="px-2 text-slate-500 text-sm">=</span>
													<span className="bg-indigo-100 px-2 py-0.5 rounded-full text-slate-600 text-sm">
														{formatPercentage(
															item.payload["MOxFQ Total Score"],
															64,
														)}
													</span>
												</span>
											</div>
											<div className="flex flex-col gap-1.5 pt-1 border-slate-100 border-t">
												<div className="flex flex-col">
													<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
														Walking/Standing Score
													</span>
													<span className="font-medium text-slate-700 text-sm">
														<span className="bg-[hsl(var(--chart-1))]/10 px-1.5 py-0.5 rounded text-slate-900">
															{item.payload["MOxFQ Walking/Standing Score"]}/28
														</span>
														<span className="mx-1 text-slate-500">=</span>
														<span className="px-1.5 py-0.5 border border-[hsl(var(--chart-1))] rounded text-slate-900">
															{formatPercentage(
																item.payload["MOxFQ Walking/Standing Score"],
																28,
															)}
														</span>
													</span>
												</div>
												<div className="flex flex-col">
													<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
														Pain Score
													</span>
													<span className="font-medium text-slate-700 text-sm">
														<span className="bg-[hsl(var(--chart-2))]/10 px-1.5 py-0.5 rounded text-slate-900">
															{item.payload["MOxFQ Pain Score"]}/20
														</span>
														<span className="mx-1 text-slate-500">=</span>
														<span className="px-1.5 py-0.5 border border-[hsl(var(--chart-2))] rounded text-slate-900">
															{formatPercentage(
																item.payload["MOxFQ Pain Score"],
																20,
															)}
														</span>
													</span>
												</div>
												<div className="flex flex-col">
													<span className="mb-1 text-[0.65rem] text-slate-600 uppercase tracking-wider">
														Social Interaction Score
													</span>
													<span className="font-medium text-slate-700 text-sm">
														<span className="bg-[hsl(var(--chart-4))]/10 px-1.5 py-0.5 rounded text-slate-900">
															{item.payload["MOxFQ Social Interaction Score"]}
															/16
														</span>
														<span className="mx-1 text-slate-500">=</span>
														<span className="px-1.5 py-0.5 border border-[hsl(var(--chart-4))] rounded text-slate-900">
															{formatPercentage(
																item.payload["MOxFQ Social Interaction Score"],
																16,
															)}
														</span>
													</span>
												</div>
											</div>
										</>
									)}
								</div>
							)}
						/>
					}
					cursor={false}
				/>
				<Area
					isAnimationActive={!disableAnimations}
					type="natural"
					dataKey="MOxFQ Walking/Standing Score"
					stroke="hsl(var(--chart-1))"
					fill="hsl(var(--chart-1))"
					stackId="moxfq"
					strokeWidth={2}
				/>
				<Area
					isAnimationActive={!disableAnimations}
					type="natural"
					dataKey="MOxFQ Pain Score"
					stroke="hsl(var(--chart-2))"
					fill="hsl(var(--chart-2))"
					stackId="moxfq"
					strokeWidth={2}
				/>
				<Area
					isAnimationActive={!disableAnimations}
					type="natural"
					dataKey="MOxFQ Social Interaction Score"
					stroke="hsl(var(--chart-4))"
					fill="hsl(var(--chart-4))"
					stackId="moxfq"
					strokeWidth={2}
				/>
			</AreaChart>
		</ChartContainer>
	);
}
